/**
 * Outbox terrain IndexedDB — GO_3_OFFLINE_TERRAIN (P1–P4)
 * Ops JSON + Blobs photos + drafts rapport.
 * Ne pas confondre avec ucp_messaging_offline_v1.
 */
const DB_NAME = 'ucp_terrain_outbox_v1';
const DB_VERSION = 1;
const STORE_OPS = 'ops';
const STORE_BLOBS = 'blobs';
const STORE_DRAFTS = 'drafts';
const MAX_OPS = 300;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_OPS)) {
        const ops = db.createObjectStore(STORE_OPS, { keyPath: 'clientOpId' });
        ops.createIndex('by_status', 'status', { unique: false });
        ops.createIndex('by_created', 'createdAt', { unique: false });
        ops.createIndex('by_scope', 'scope', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: 'blobId' });
      }
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS, { keyPath: 'draftKey' });
      }
    };
  });
}

function newId(prefix = 'op') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('tx aborted'));
  });
}

export async function putTerrainBlob({ blobId, blob, mime, filename, fieldName, clientOpId }) {
  const db = await openDb();
  const tx = db.transaction(STORE_BLOBS, 'readwrite');
  tx.objectStore(STORE_BLOBS).put({
    blobId,
    blob,
    mime: mime || blob?.type || 'application/octet-stream',
    filename: filename || 'file',
    fieldName: fieldName || 'file',
    clientOpId: clientOpId || null,
    createdAt: Date.now(),
    size: blob?.size || 0
  });
  await txDone(tx);
  return blobId;
}

export async function getTerrainBlob(blobId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_BLOBS, 'readonly');
    const req = tx.objectStore(STORE_BLOBS).get(blobId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteTerrainBlob(blobId) {
  const db = await openDb();
  const tx = db.transaction(STORE_BLOBS, 'readwrite');
  tx.objectStore(STORE_BLOBS).delete(blobId);
  await txDone(tx);
}

/**
 * Sérialise config axios (JSON ou FormData) pour stockage IDB.
 * @returns {Promise<{ payloadKind: string, payload: object, blobIds: string[] }>}
 */
export async function serializeAxiosConfigData(data, clientOpId) {
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const fields = {};
    const blobIds = [];
    const fileMeta = [];
    for (const [key, value] of data.entries()) {
      if (typeof Blob !== 'undefined' && value instanceof Blob) {
        const blobId = newId('blob');
        const filename = value.name || `${key}.bin`;
        await putTerrainBlob({
          blobId,
          blob: value,
          mime: value.type,
          filename,
          fieldName: key,
          clientOpId
        });
        blobIds.push(blobId);
        fileMeta.push({ blobId, fieldName: key, filename, mime: value.type });
      } else {
        if (Object.prototype.hasOwnProperty.call(fields, key)) {
          const prev = fields[key];
          fields[key] = Array.isArray(prev) ? [...prev, value] : [prev, value];
        } else {
          fields[key] = value;
        }
      }
    }
    return {
      payloadKind: 'multipart',
      payload: { fields, fileMeta },
      blobIds
    };
  }

  return {
    payloadKind: 'json',
    payload: { data: data == null ? null : data },
    blobIds: []
  };
}

/** Reconstruit data axios depuis op sérialisée. */
export async function rebuildAxiosConfigData(op) {
  if (!op) return undefined;
  if (op.payloadKind === 'multipart') {
    const form = new FormData();
    const fields = op.payload?.fields || {};
    Object.keys(fields).forEach((key) => {
      const val = fields[key];
      if (Array.isArray(val)) val.forEach((v) => form.append(key, v));
      else form.append(key, val);
    });
    const fileMeta = op.payload?.fileMeta || [];
    for (const meta of fileMeta) {
      const row = await getTerrainBlob(meta.blobId);
      if (!row?.blob) continue;
      const file = row.blob instanceof File
        ? row.blob
        : new File([row.blob], meta.filename || row.filename || 'file', {
          type: meta.mime || row.mime || 'application/octet-stream'
        });
      form.append(meta.fieldName || row.fieldName || 'files', file);
    }
    return form;
  }
  return op.payload?.data;
}

export async function enqueueTerrainOp(partial) {
  const clientOpId = partial.clientOpId || newId('op');
  const serialized = partial.payloadKind
    ? {
      payloadKind: partial.payloadKind,
      payload: partial.payload,
      blobIds: partial.blobIds || []
    }
    : await serializeAxiosConfigData(partial.data, clientOpId);

  const op = {
    clientOpId,
    createdAt: Date.now(),
    scope: partial.scope || 'anonymous',
    method: String(partial.method || 'post').toLowerCase(),
    url: partial.url || '',
    params: partial.params || null,
    headers: partial.headers || {},
    status: 'pending',
    attempts: 0,
    lastError: null,
    dependsOn: Array.isArray(partial.dependsOn) ? partial.dependsOn : [],
    opType: partial.opType || 'http',
    entityType: partial.entityType || null,
    entityId: partial.entityId || null,
    ...serialized
  };

  const db = await openDb();
  const tx = db.transaction(STORE_OPS, 'readwrite');
  const store = tx.objectStore(STORE_OPS);
  const allReq = store.getAll();
  await new Promise((resolve, reject) => {
    allReq.onsuccess = () => resolve();
    allReq.onerror = () => reject(allReq.error);
  });
  const existing = allReq.result || [];
  if (existing.length >= MAX_OPS) {
    const sorted = [...existing].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    const overflow = sorted.slice(0, existing.length - MAX_OPS + 1);
    overflow.forEach((old) => store.delete(old.clientOpId));
  }
  store.put(op);
  await txDone(tx);
  return op;
}

export async function listTerrainOps({ status = null, scope = null } = {}) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_OPS, 'readonly');
    const req = tx.objectStore(STORE_OPS).getAll();
    req.onsuccess = () => {
      let rows = req.result || [];
      if (status) rows = rows.filter((r) => r.status === status);
      if (scope) rows = rows.filter((r) => !r.scope || r.scope === scope);
      rows.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function updateTerrainOp(clientOpId, patch) {
  const db = await openDb();
  const tx = db.transaction(STORE_OPS, 'readwrite');
  const store = tx.objectStore(STORE_OPS);
  const getReq = store.get(clientOpId);
  await new Promise((resolve, reject) => {
    getReq.onsuccess = () => resolve();
    getReq.onerror = () => reject(getReq.error);
  });
  const row = getReq.result;
  if (!row) {
    await txDone(tx);
    return null;
  }
  const next = { ...row, ...patch };
  store.put(next);
  await txDone(tx);
  return next;
}

export async function removeTerrainOp(clientOpId) {
  const db = await openDb();
  const op = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_OPS, 'readonly');
    const req = tx.objectStore(STORE_OPS).get(clientOpId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
  const blobIds = op?.blobIds || op?.payload?.fileMeta?.map((m) => m.blobId) || [];
  const tx = db.transaction([STORE_OPS, STORE_BLOBS], 'readwrite');
  tx.objectStore(STORE_OPS).delete(clientOpId);
  blobIds.forEach((id) => {
    if (id) tx.objectStore(STORE_BLOBS).delete(id);
  });
  await txDone(tx);
}

export async function clearTerrainOutbox() {
  const db = await openDb();
  const tx = db.transaction([STORE_OPS, STORE_BLOBS, STORE_DRAFTS], 'readwrite');
  tx.objectStore(STORE_OPS).clear();
  tx.objectStore(STORE_BLOBS).clear();
  // Conserver les drafts locaux ? Non — session end purge tout pour anti-boucle auth.
  tx.objectStore(STORE_DRAFTS).clear();
  await txDone(tx);
}

export async function countPendingTerrainOps(scope = null) {
  const rows = await listTerrainOps({ status: 'pending', scope });
  return rows.length;
}

/**
 * Replay FIFO des ops pending (scope courant).
 * @param {(op: object) => Promise<void>} sendFn
 */
export async function flushTerrainOutbox(sendFn, { scope = null } = {}) {
  const pending = await listTerrainOps({ status: 'pending', scope });
  for (const op of pending) {
    try {
      await updateTerrainOp(op.clientOpId, { status: 'inflight', attempts: (op.attempts || 0) + 1 });
      await sendFn(op);
      await removeTerrainOp(op.clientOpId);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 408 || status === 429) {
        await updateTerrainOp(op.clientOpId, {
          status: 'pending',
          lastError: error?.message || 'deferred'
        });
        break;
      }
      if (status && status >= 400 && status < 500) {
        await updateTerrainOp(op.clientOpId, {
          status: 'failed',
          lastError: error?.response?.data?.error?.message || error?.message || '4xx'
        });
        // Ne bloque pas la suite pour une op métier invalide
        continue;
      }
      await updateTerrainOp(op.clientOpId, {
        status: 'pending',
        lastError: error?.message || 'network'
      });
      break;
    }
  }
}

/** Draft rapport local (P3) */
export async function saveReportDraft(draftKey, draft) {
  const db = await openDb();
  const tx = db.transaction(STORE_DRAFTS, 'readwrite');
  tx.objectStore(STORE_DRAFTS).put({
    draftKey,
    ...draft,
    updatedAt: Date.now()
  });
  await txDone(tx);
}

export async function getReportDraft(draftKey) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_DRAFTS, 'readonly');
    const req = tx.objectStore(STORE_DRAFTS).get(draftKey);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteReportDraft(draftKey) {
  const db = await openDb();
  const tx = db.transaction(STORE_DRAFTS, 'readwrite');
  tx.objectStore(STORE_DRAFTS).delete(draftKey);
  await txDone(tx);
}

export { newId as newTerrainId };
